/**
 * @component ry-hero
 * @category  layout
 * @summary   Hero section wrapper — a CSS-only layout container for the top-of-page headline area (ry-ui-only; no Fomantic equivalent). Pure styling hook with `align` for text alignment; emits nothing, no JS behavior. Children are kept verbatim.
 *
 * @attribute align   'left' | 'center' | 'right' ?   default="left"   Text alignment within the hero. Pure CSS hook — the theme reads this attribute and applies `text-align`.
 * @attribute size    'sm' | 'lg' ?                    Pure CSS hook for compact / oversized hero padding.
 *
 * @content   Free-form. Typically `<h1>` + `<p>` + an action cluster (`<ry-cluster>` of `<ry-button>`s). The component keeps children unchanged.
 *
 * @dom
 *   ry-hero[align?][size?]
 *     > {authored children}
 *
 * @gotcha  No JS — `<ry-hero>` is just a styling hook. Without the theme CSS loaded, it renders inline with no special padding or alignment.
 *
 * @gotcha  `align` only controls text-align. To CENTER a button cluster (which is a flex container), the cluster itself needs centering — either set `align="center"` on `<ry-cluster>` or use the appropriate layout primitive.
 *
 * @gotcha  Setup is a no-op. Authoring with extra layers inside (e.g. nested `<ry-hero>`) silently works but indicates a likely architectural mistake — flatten before mount.
 *
 * @example basic
 * ```html
 * <ry-hero>
 *   <h1>Build apps faster</h1>
 *   <p>Framework-agnostic components.</p>
 * </ry-hero>
 * ```
 *
 * @example centered-with-actions
 * ```html
 * <ry-hero align="center">
 *   <h1>Build apps faster</h1>
 *   <p>Framework-agnostic components.</p>
 *   <ry-cluster>
 *     <ry-button variant="primary">Get Started</ry-button>
 *     <ry-button>Docs</ry-button>
 *   </ry-cluster>
 * </ry-hero>
 * ```
 *
 * @example large
 * ```html
 * <ry-hero size="lg" align="center">
 *   <h1>Hero with extra breathing room</h1>
 *   <p>Use size="lg" for landing pages.</p>
 * </ry-hero>
 * ```
 *
 * @example right-aligned
 * ```html
 * <ry-hero align="right">
 *   <h1>Aligned to the right edge</h1>
 *   <p>Common in alternating-section layouts.</p>
 * </ry-hero>
 * ```
 *
 * @tutorial mounts-without-transform
 * ```js
 * // Wrong assumption: ry-hero wraps or transforms its children. It does NOT —
 * // it's purely a styling hook with no JS behavior.
 * const el = mount(`
 *   <ry-hero>
 *     <h1 id="h">Title</h1>
 *     <p id="p">Lead text</p>
 *   </ry-hero>
 * `);
 * await expectEqual(el.children.length, 2, 'children preserved');
 * await expectEqual(el.querySelector('#h')?.tagName, 'H1');
 * await expectEqual(el.querySelector('#p')?.tagName, 'P');
 * ```
 *
 * @tutorial align-attribute-survives
 * ```js
 * const el = mount('<ry-hero align="center"><h1>X</h1></ry-hero>');
 * await expectAttr(el, 'align', 'center', 'align attribute reflected on host for CSS');
 * ```
 *
 * @tutorial size-attribute-survives
 * ```js
 * const el = mount('<ry-hero size="lg"><h1>X</h1></ry-hero>');
 * await expectAttr(el, 'size', 'lg');
 * ```
 *
 * @tutorial combined-align-and-size
 * ```js
 * const el = mount('<ry-hero align="right" size="sm"><h1>X</h1></ry-hero>');
 * await expectAttr(el, 'align', 'right');
 * await expectAttr(el, 'size', 'sm');
 * ```
 *
 * @tutorial empty-hero-is-graceful
 * ```js
 * // Wrong assumption: empty hero throws or warns. It silently mounts.
 * const el = mount('<ry-hero></ry-hero>');
 * await expectEqual(el.children.length, 0);
 * ```
 *
 * @see ry-feature-grid
 * @see ry-cluster
 */

import { RyElement } from '../core/ry-element.js';

export class RyHero extends RyElement {}

customElements.define('ry-hero', RyHero);
